import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { capitalize } from 'lodash';
import { Button, RichText, TEInput } from '@timeedit/ui-components';
import { Form } from '@timeedit/ui-components/antd';
import type { Rule } from '@timeedit/ui-components/antd/form/index';
import type { Store } from '@timeedit/ui-components/antd/form/interface';
import type { DefaultOptionType } from '@timeedit/ui-components/antd/select/index';
import { EWidgetType } from '../../Widget';

interface WidgetInterfaceProps {
  key: string;
  required: boolean;
  value?: string;
  type: 'tuple' | 'union' | 'array' | 'string';
  props?: WidgetInterfaceProps[];
}

interface WidgetInterface {
  name: string;
  props?: WidgetInterfaceProps[];
}

const DynamicFormFields = ({
  type,
  propKey,
  props,
  required,
  id,
  name = propKey,
  className = '',
}: Omit<WidgetInterfaceProps, 'key'> & {
  id: string;
  propKey: string;
  name?: string;
  className?: string;
}) => {
  const rules = useMemo(() => {
    const rules: Rule[] = [{ required, message: `Please enter ${propKey}` }];
    if (propKey === 'url') {
      rules.push({
        pattern: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[^\s]*)?$/i,
        message: `This is not a valid url`,
      });
    }

    return rules;
  }, [required, propKey]);

  return (
    <Form.Item
      className={`form-item-row ${className}`}
      name={name}
      key={`dynamic-prop-${propKey}-${id}`}
      rules={rules}
    >
      {type === 'string' && <TEInput.Input label={capitalize(propKey)} />}
      {(type === 'tuple' || type === 'union') && (
        <TEInput.Select
          options={(props as WidgetInterfaceProps[]).map(({ type, value }) => ({
            label: type,
            value,
          }))}
        />
      )}
    </Form.Item>
  );
};

export default function WidgetFormFields({
  initialValues,
}: {
  initialValues: Store | undefined;
}) {
  const id = useId();
  const [widgets, setWidgets] = useState<DefaultOptionType[]>([]);
  useEffect(() => {
    const allWidgets = Object.keys(EWidgetType).map((name) => ({
      value: name,
      label: name,
    }));
    setWidgets(allWidgets);
  }, []);

  const { getFieldValue, getFieldsValue, setFieldsValue } =
    Form.useFormInstance();
  const [activeWidget, setActiveWidget] = useState<keyof typeof EWidgetType>(
    getFieldValue('type')
  );
  const activeWidgetProps = useMemo(() => {
    if (!activeWidget) return;
    const { props } = EWidgetType[activeWidget] as unknown as WidgetInterface;

    return props;
  }, [activeWidget]);

  const initialArrayFormItems = useMemo(() => {
    if (!initialValues) return {};

    const formItems: Record<string, number> = {};
    Object.keys(initialValues).map((key) => {
      if (key.startsWith('array-')) {
        const [, index, name] = key.split('-');
        formItems[name] = Number(index) + 1;
      }
    });

    return formItems;
  }, [initialValues]);

  const [arrayFormItems, setArrayFormItems] = useState<Record<string, number>>(
    initialArrayFormItems
  );

  const updateArrayFormItems = useCallback(
    (key: string, delta: number) =>
      setArrayFormItems((prev) => {
        const count = prev[key] ?? 1;
        const newCount = Math.max(1, count + delta);
        return { ...prev, [key]: newCount };
      }),
    [setArrayFormItems]
  );

  const [lexicalValues, setLexicalValues] = useState<Record<string, string>>();
  useEffect(() => {
    if (!lexicalValues) return;

    const { name, value } = lexicalValues;
    setFieldsValue({ ...getFieldsValue(), [name]: value });
  }, [getFieldsValue, lexicalValues, setFieldsValue]);
  return (
    <>
      <Form.Item
        name="title"
        rules={[{ required: true }]}
        className="form-item-row"
      >
        <TEInput.Input label="Title" />
      </Form.Item>
      <Form.Item
        name="type"
        rules={[{ required: true }]}
        className="form-item-row"
      >
        <TEInput.Select
          label="Type"
          options={widgets}
          onChange={(value: keyof typeof EWidgetType) => setActiveWidget(value)}
        />
      </Form.Item>
      {activeWidgetProps &&
        activeWidgetProps.map(({ key, required, type, props }, index) => (
          <Fragment key={`${key}-${type}-${index}`}>
            {type === 'string' && key === 'content' && (
              <Form.Item
                key={`active-widget-prop-${index}-${key}-${id}`}
                className="form-item-row"
                name={key}
                rules={[{ required, message: 'Please enter content' }]}
              >
                <div>
                  <label htmlFor={key}>Content:</label>
                  <RichText
                    initialValue={getFieldValue('content')}
                    onBlur={(value) => {
                      setLexicalValues({ name: key, value });
                    }}
                  />
                </div>
              </Form.Item>
            )}
            {type === 'array' && (
              <>
                {[...Array(arrayFormItems?.[key] || 1).keys()].map((index) => (
                  <div key={`${key}-${type}-${index}`}>
                    <b>
                      {capitalize(key)} {index + 1}
                    </b>
                    <div className="form-item-list">
                      {((props as WidgetInterfaceProps[]) ?? []).map(
                        ({ key: propKey, type, required }) => {
                          const id = `${key}-${propKey}-${type}-${index}`;
                          return (
                            <DynamicFormFields
                              key={id}
                              id={id}
                              propKey={propKey}
                              name={`array-${index}-${key}-${propKey}`}
                              type={type}
                              props={props}
                              required={required}
                            />
                          );
                        }
                      )}
                    </div>
                  </div>
                ))}
                <div className="form-item-row button-group">
                  <Button onClick={() => updateArrayFormItems(key, +1)}>
                    <>Add {key}</>
                  </Button>
                  <Button
                    danger
                    disabled={(arrayFormItems[key] ?? 1) <= 1}
                    onClick={() => updateArrayFormItems(key, -1)}
                  >
                    <>Remove last {key}</>
                  </Button>
                </div>
              </>
            )}
            {type === 'tuple' && (
              <DynamicFormFields
                propKey={key}
                type={type}
                id={`${key}-${type}`}
                props={props}
                required={required}
              />
            )}
            {type === 'union' && (
              <DynamicFormFields
                propKey={key}
                type={type}
                id={`${key}-${type}`}
                props={props}
                required={required}
              />
            )}
          </Fragment>
        ))}
      <Form.Item className="button-wrapper">
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </>
  );
}
